/* eslint-disable max-lines */
import type { DragEvent, MouseEvent as ReactMouseEvent } from "react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import {
  Background,
  Controls,
  MiniMap,
  Panel,
  ReactFlow,
  ReactFlowProvider,
  useReactFlow,
} from "@xyflow/react"
import { toast } from "sonner"

import type { ComponentItem } from "../constants/workflow-components"
import type { EdgeTypes, Node, NodeTypes } from "@xyflow/react"

import "@xyflow/react/dist/style.css"
import "../styles/workflow-editor.css"
import {
  AlignHorizontalDistributeCenter,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Search,
  X,
} from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Separator } from "@/components/ui/separator"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { useViewportBelow } from "@/hooks/use-viewport-below"
import { cn } from "@/lib/utils"
import { SettingsService } from "@/services/settings-service"

const SIDEBAR_COLLAPSED_KEY = "workflow-components-sidebar-collapsed"
const CATEGORIES_COLLAPSED_KEY = "workflow-components-collapsed-categories"

// Short, human descriptions shown on hover for each palette item (#11 "mouseover
// on all tools"). Keyed by node type; the generic "tool" items fall back to their
// own data.description, and anything unmapped falls back to the label.
const NODE_DESCRIPTIONS: Record<string, string> = {
  start: "Entry point — set whether the flow needs text or file input.",
  end: "Where the flow finishes. A flow needs at least one.",
  conditional: "Branch true/false from a condition (AI or expression).",
  loopContainer: "Run its child nodes once per item, or a fixed number of times.",
  parallel: "Split the flow into concurrent branches (pair with Merge).",
  merge: "Combine the outputs of parallel branches.",
  delay: "Pause the flow for a set time.",
  llm: "Transform the input with a single LLM prompt.",
  agent: "Autonomous agent with tools (web search, libraries, M365…).",
  variable: "Store or recall a value across the flow.",
  format: "Wrap the output in a fixed template using {input}.",
  email: "Email the current output — works headlessly (no user sign-in).",
  factcheck: "Verify the input against a claim — outputs a pass/fail verdict.",
  userInput: "Collect input from the user at run time.",
  attachment: "Add a file's extracted text into the flow's context.",
  approval: "Pause a durable run for human approval before continuing.",
}

import { LabeledEdge } from "./labeled-edge"
import {
  CATEGORY_LABELS,
  CATEGORY_ORDER,
  WORKFLOW_COMPONENTS,
} from "../constants/workflow-components"
import { useWorkflowStore } from "../stores/workflow-store"
import { layoutWorkflowGraph } from "../utils/workflow-auto-layout"
import { getFlowEmailDisabledReason } from "../utils/workflow-email-capability"
import { AgentNode } from "./nodes/agent-node"
import { ApprovalNode } from "./nodes/approval-node"
import { AttachmentNode } from "./nodes/attachment-node"
import { ConditionalNode } from "./nodes/conditional-node"
import { DelayNode } from "./nodes/delay-node"
import { EmailNode } from "./nodes/email-node"
import { EndNode } from "./nodes/end-node"
import { FactCheckNode } from "./nodes/factcheck-node"
import { FormatNode } from "./nodes/format-node"
import { HttpNode } from "./nodes/http-node"
import { LLMNode } from "./nodes/llm-node"
import { LoopContainerNode } from "./nodes/loop-container-node"
import { LoopEndNode } from "./nodes/loop-end-node"
import { LoopNode } from "./nodes/loop-node"
import { LoopStartNode } from "./nodes/loop-start-node"
import { MergeNode } from "./nodes/merge-node"
import { ParallelNode } from "./nodes/parallel-node"
import { RetryNode } from "./nodes/retry-node"
import { StartNode } from "./nodes/start-node"
import { ToolNode } from "./nodes/tool-node"
import { UserInputNode } from "./nodes/user-input-node"
import { VariableNode } from "./nodes/variable-node"

const nodeTypes: NodeTypes = {
  llm: LLMNode,
  start: StartNode,
  end: EndNode,
  tool: ToolNode,
  agent: AgentNode,
  conditional: ConditionalNode,
  loop: LoopNode,
  loopContainer: LoopContainerNode,
  loopStart: LoopStartNode,
  loopEnd: LoopEndNode,
  retry: RetryNode,
  delay: DelayNode,
  parallel: ParallelNode,
  merge: MergeNode,
  variable: VariableNode,
  format: FormatNode,
  email: EmailNode,
  http: HttpNode,
  factcheck: FactCheckNode,
  attachment: AttachmentNode,
  userInput: UserInputNode,
  approval: ApprovalNode,
}

const edgeTypes: EdgeTypes = {
  labeled: LabeledEdge,
}

interface WorkflowCanvasProps {
  activeNodeId?: string | null
  readOnly?: boolean
  pendingComponentAdd?: { requestId: number; component: ComponentItem } | null
  onPendingComponentAdded?: (requestId: number) => void
}

function WorkflowCanvas({
  activeNodeId,
  readOnly = false,
  pendingComponentAdd = null,
  onPendingComponentAdded,
}: WorkflowCanvasProps) {
  const {
    nodes,
    edges,
    onNodesChange,
    onEdgesChange,
    onConnect,
    setNodes,
    setEdges,
    applyGraphUpdate,
    setActiveNode,
    setReadOnly,
    setDropTargetLoopId,
    setInvalidConnectionHighlight,
    undo,
    redo,
    canUndo,
    canRedo,
    autoLayoutRequestId,
  } = useWorkflowStore()
  const { screenToFlowPosition } = useReactFlow()
  const lastAutoLayoutRequestRef = useRef(0)

  // Sync active node from props to store
  useEffect(() => {
    setActiveNode(activeNodeId || null)
  }, [activeNodeId, setActiveNode])

  // Sync readOnly mode to store for edge components
  useEffect(() => {
    setReadOnly(readOnly)
  }, [readOnly, setReadOnly])

  useEffect(() => {
    if (autoLayoutRequestId <= lastAutoLayoutRequestRef.current) return
    if (nodes.length === 0) return

    const hasLayoutDimensions = nodes.every((node) => {
      const width = node.measured?.width ?? node.width
      const height = node.measured?.height ?? node.height
      return typeof width === "number" && typeof height === "number"
    })

    if (!hasLayoutDimensions) return

    const layoutedResult = layoutWorkflowGraph(nodes, edges, { direction: "LR" })
    applyGraphUpdate(layoutedResult.nodes, layoutedResult.edges)
    lastAutoLayoutRequestRef.current = autoLayoutRequestId
  }, [autoLayoutRequestId, nodes, edges, applyGraphUpdate])

  // Keyboard shortcuts for undo/redo
  useEffect(() => {
    const isEditableTarget = (target: EventTarget | null) => {
      if (!(target instanceof HTMLElement)) return false
      if (target.isContentEditable) return true
      const tag = target.tagName
      return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT"
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (isEditableTarget(e.target)) return
      if ((e.ctrlKey || e.metaKey) && e.key === "z" && !e.shiftKey) {
        if (canUndo()) {
          e.preventDefault()
          undo()
        }
      }
      if ((e.ctrlKey || e.metaKey) && (e.key === "y" || (e.key === "z" && e.shiftKey))) {
        if (canRedo()) {
          e.preventDefault()
          redo()
        }
      }
    }

    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [undo, redo, canUndo, canRedo])

  // Helper to find if position is inside a loop container
  const findLoopContainerAtPosition = useCallback(
    (position: { x: number; y: number }, excludeNodeId?: string) => {
      const containerNodes = nodes.filter(
        (n) => n.type === "loopContainer" && n.id !== excludeNodeId
      )
      for (const container of containerNodes) {
        const containerWidth = (container.data.width as number) || 500
        const containerHeight = (container.data.height as number) || 350
        const containerX = container.position.x
        const containerY = container.position.y
        // Header (~40px) + settings panel (~120px) = drop zone starts at ~160px
        const headerAndSettingsHeight = 160

        if (
          position.x >= containerX &&
          position.x <= containerX + containerWidth &&
          position.y >= containerY + headerAndSettingsHeight &&
          position.y <= containerY + containerHeight
        ) {
          return container.id
        }
      }
      return null
    },
    [nodes]
  )

  // Track the node being dragged for the onNodeDragStop handler
  const draggingNodeRef = useRef<Node | null>(null)

  // Handle dragging existing nodes - detect when hovering over loop containers
  const onNodeDrag = useCallback(
    (_event: ReactMouseEvent, node: Node) => {
      if (readOnly) return

      // Don't allow loop containers to be nested
      if (node.type === "loopContainer") {
        setDropTargetLoopId(null)
        return
      }

      // Store the dragging node for use in onNodeDragStop
      draggingNodeRef.current = node

      // Calculate absolute position for nodes that are children of a loop
      let absolutePosition = node.position
      if (node.parentId) {
        const parentNode = nodes.find((n) => n.id === node.parentId)
        if (parentNode) {
          absolutePosition = {
            x: parentNode.position.x + node.position.x,
            y: parentNode.position.y + node.position.y,
          }
        }
      }

      // Check if dragging over a loop container (exclude the node's current parent to allow moving within)
      const loopId = findLoopContainerAtPosition(absolutePosition, node.id)

      // Only show drop target if it's a different loop than current parent
      if (loopId && loopId !== node.parentId) {
        setDropTargetLoopId(loopId)
      } else if (!loopId && node.parentId) {
        // Dragging out of a loop - could show a different indicator
        setDropTargetLoopId(null)
      } else {
        setDropTargetLoopId(null)
      }
    },
    [readOnly, nodes, findLoopContainerAtPosition, setDropTargetLoopId]
  )

  // Handle dropping existing nodes into loops
  const onNodeDragStop = useCallback(
    (_event: ReactMouseEvent, node: Node) => {
      if (readOnly) return

      // Clear the ref
      draggingNodeRef.current = null

      // Don't allow loop containers to be nested
      if (node.type === "loopContainer") {
        setDropTargetLoopId(null)
        return
      }

      // Calculate absolute position for nodes that are children of a loop
      let absolutePosition = node.position
      const currentParentId = node.parentId
      if (currentParentId) {
        const parentNode = nodes.find((n) => n.id === currentParentId)
        if (parentNode) {
          absolutePosition = {
            x: parentNode.position.x + node.position.x,
            y: parentNode.position.y + node.position.y,
          }
        }
      }

      const loopId = findLoopContainerAtPosition(absolutePosition, node.id)

      // Clear drop target immediately
      setDropTargetLoopId(null)

      // Case 1: Moving into a new loop
      if (loopId && loopId !== currentParentId) {
        const targetContainer = nodes.find((n) => n.id === loopId)
        if (!targetContainer) return

        // Calculate position relative to the new container
        const relativePosition = {
          x: absolutePosition.x - targetContainer.position.x,
          y: absolutePosition.y - targetContainer.position.y,
        }

        // Update the node to be a child of the loop
        const updatedNodes = nodes.map((n) => {
          if (n.id === node.id) {
            return {
              ...n,
              position: relativePosition,
              parentId: loopId,
              extent: "parent" as const,
            }
          }
          // Update old parent's childNodes array if moving from another loop
          if (currentParentId && n.id === currentParentId) {
            const oldChildNodes = (n.data.childNodes as string[] | undefined) || []
            return {
              ...n,
              data: {
                ...n.data,
                childNodes: oldChildNodes.filter((id) => id !== node.id),
              },
            }
          }
          // Update new parent's childNodes array
          if (n.id === loopId) {
            const childNodes = (n.data.childNodes as string[] | undefined) || []
            if (!childNodes.includes(node.id)) {
              return {
                ...n,
                data: {
                  ...n.data,
                  childNodes: [...childNodes, node.id],
                },
              }
            }
          }
          return n
        })

        // Remove any edges that cross the loop boundary
        const updatedEdges = edges.filter((edge) => {
          const sourceNode = updatedNodes.find((n) => n.id === edge.source)
          const targetNode = updatedNodes.find((n) => n.id === edge.target)
          if (!sourceNode || !targetNode) return true
          // Keep edge only if both nodes are in the same scope (same parent or both no parent)
          return sourceNode.parentId === targetNode.parentId
        })

        setNodes(updatedNodes)
        setEdges(updatedEdges)
      }
      // Case 2: Moving out of a loop
      else if (!loopId && currentParentId) {
        // Update the node to be outside any loop
        const updatedNodes = nodes.map((n) => {
          if (n.id === node.id) {
            // Remove parentId and extent, keep absolute position
            const { parentId, extent, ...rest } = n
            return {
              ...rest,
              position: absolutePosition,
            }
          }
          // Update old parent's childNodes array
          if (n.id === currentParentId) {
            const oldChildNodes = (n.data.childNodes as string[] | undefined) || []
            return {
              ...n,
              data: {
                ...n.data,
                childNodes: oldChildNodes.filter((id) => id !== node.id),
              },
            }
          }
          return n
        })

        // Remove any edges that were internal to the loop
        const updatedEdges = edges.filter((edge) => {
          const sourceNode = updatedNodes.find((n) => n.id === edge.source)
          const targetNode = updatedNodes.find((n) => n.id === edge.target)
          if (!sourceNode || !targetNode) return true
          // Keep edge only if both nodes are in the same scope
          return sourceNode.parentId === targetNode.parentId
        })

        setNodes(updatedNodes)
        setEdges(updatedEdges)
      }
    },
    [readOnly, nodes, edges, findLoopContainerAtPosition, setDropTargetLoopId, setNodes, setEdges]
  )

  const onDragOver = useCallback(
    (event: DragEvent) => {
      if (readOnly) return
      event.preventDefault()
      event.dataTransfer.dropEffect = "move"

      // Check if we're dragging a node type
      const isDraggingNode = event.dataTransfer.types.includes("application/reactflow/type")
      if (!isDraggingNode) return

      // Detect if hovering over a loop container
      const position = screenToFlowPosition({ x: event.clientX, y: event.clientY })
      const loopId = findLoopContainerAtPosition(position)
      setDropTargetLoopId(loopId)
    },
    [readOnly, screenToFlowPosition, findLoopContainerAtPosition, setDropTargetLoopId]
  )

  const onDragLeave = useCallback(() => {
    setDropTargetLoopId(null)
  }, [setDropTargetLoopId])

  const addComponentNode = useCallback(
    (type: string, data: Record<string, unknown>, position: { x: number; y: number }) => {
      if (readOnly) return

      let parentId: string | undefined
      let relativePosition = position

      if (type !== "loopContainer") {
        const loopId = findLoopContainerAtPosition(position)
        if (loopId) {
          const container = nodes.find((n) => n.id === loopId)
          if (container) {
            parentId = loopId
            relativePosition = {
              x: position.x - container.position.x,
              y: position.y - container.position.y,
            }
          }
        }
      }

      const newNodeId = `${type}-${Date.now()}`
      const newNode = {
        id: newNodeId,
        type,
        position: relativePosition,
        data,
        ...(parentId && { parentId, extent: "parent" as const }),
      }

      if (parentId) {
        const updatedNodes = nodes.map((node) =>
          node.id === parentId
            ? {
                ...node,
                data: {
                  ...node.data,
                  childNodes: [
                    ...((node.data.childNodes as string[] | undefined) || []),
                    newNodeId,
                  ],
                },
              }
            : node
        )
        setNodes([...updatedNodes, newNode])
      } else {
        setNodes([...nodes, newNode])
      }
    },
    [nodes, readOnly, findLoopContainerAtPosition, setNodes]
  )

  const onDrop = useCallback(
    (event: DragEvent) => {
      if (readOnly) return
      event.preventDefault()

      // Clear drop target state
      setDropTargetLoopId(null)

      const type = event.dataTransfer.getData("application/reactflow/type")
      const toolData = event.dataTransfer.getData("application/reactflow/data")

      if (typeof type === "undefined" || !type) {
        return
      }

      let parsedData = { label: `${type} node` }
      if (toolData) {
        try {
          parsedData = JSON.parse(toolData)
        } catch {
          // Ignore
        }
      }

      const position = screenToFlowPosition({ x: event.clientX, y: event.clientY })
      addComponentNode(type, parsedData, position)
    },
    [readOnly, screenToFlowPosition, setDropTargetLoopId, addComponentNode]
  )

  useEffect(() => {
    if (!pendingComponentAdd) return
    const { requestId, component } = pendingComponentAdd
    if (!readOnly && !component.disabled) {
      const position = screenToFlowPosition({
        x: window.innerWidth / 2,
        y: window.innerHeight / 2,
      })
      addComponentNode(
        component.type,
        component.data ?? { label: `${component.type} node` },
        position
      )
    }
    onPendingComponentAdded?.(requestId)
  }, [
    addComponentNode,
    onPendingComponentAdded,
    pendingComponentAdd,
    readOnly,
    screenToFlowPosition,
  ])

  const defaultEdgeOptions = {
    style: { strokeWidth: 2, stroke: "hsl(var(--muted-foreground))" },
    type: "labeled",
  }

  // Custom onConnect that adds labels for conditional edges and validates loop connections
  const handleConnect = useCallback(
    (connection: Parameters<typeof onConnect>[0]) => {
      if (readOnly) return

      const sourceNode = nodes.find((n) => n.id === connection.source)
      const targetNode = nodes.find((n) => n.id === connection.target)

      // Top-level self-loops are invalid cycles. Loop children may self-connect —
      // validation intentionally excludes Loop interiors from cycle detection.
      if (connection.source === connection.target) {
        const parentId = sourceNode?.parentId
        const parentIsLoop =
          typeof parentId === "string" &&
          nodes.some((node) => node.id === parentId && node.type === "loopContainer")
        if (!parentIsLoop) return
      }

      // Prevent external edges to loop children - edges should connect to the loop container
      const sourceParentId = sourceNode?.parentId
      const targetParentId = targetNode?.parentId

      // If connecting TO a loop child from outside, block it
      if (targetParentId !== undefined && sourceParentId !== targetParentId) {
        toast.error("Cannot connect directly to nodes inside a loop", {
          description: "Connect to the loop's input handle on the left edge instead",
        })
        // Highlight the loop container's input handle
        setInvalidConnectionHighlight(targetParentId, "input")
        setTimeout(() => setInvalidConnectionHighlight(null, null), 3000)
        return
      }

      // If connecting FROM a loop child to outside, block it
      if (sourceParentId !== undefined && targetParentId !== sourceParentId) {
        toast.error("Cannot connect directly from nodes inside a loop", {
          description: "Connect from the loop's output handle on the right edge instead",
        })
        // Highlight the loop container's output handle
        setInvalidConnectionHighlight(sourceParentId, "output")
        setTimeout(() => setInvalidConnectionHighlight(null, null), 3000)
        return
      }

      // Check if source is a conditional node
      if (sourceNode?.type === "conditional" && connection.sourceHandle) {
        // Add labeled edge with true/false label
        const label = connection.sourceHandle === "true" ? "true" : "false"
        const newEdge = {
          ...connection,
          id: `${connection.source}-${connection.target}-${Date.now()}`,
          type: "labeled",
          data: { label },
        }
        setEdges([...edges, newEdge])
      } else {
        onConnect(connection)
      }
    },
    [nodes, edges, setEdges, onConnect, readOnly, setInvalidConnectionHighlight]
  )

  const handleAutoFormat = useCallback(() => {
    if (readOnly || nodes.length === 0) return
    const { nodes: layoutedNodes } = layoutWorkflowGraph(nodes, edges, { direction: "LR" })
    setNodes(layoutedNodes)
  }, [nodes, edges, setNodes, readOnly])

  return (
    <ReactFlow
      edgesReconnectable
      fitView
      className={cn("workflow-canvas", readOnly && "workflow-readonly")}
      defaultEdgeOptions={defaultEdgeOptions}
      deleteKeyCode={readOnly ? null : ["Backspace", "Delete"]}
      edges={edges}
      edgeTypes={edgeTypes}
      nodes={nodes}
      nodesConnectable={!readOnly}
      nodesDraggable={!readOnly}
      nodeTypes={nodeTypes}
      onConnect={readOnly ? undefined : handleConnect}
      onDragLeave={readOnly ? undefined : onDragLeave}
      onDragOver={readOnly ? undefined : onDragOver}
      onDrop={readOnly ? undefined : onDrop}
      onEdgesChange={readOnly ? undefined : onEdgesChange}
      onNodeDrag={readOnly ? undefined : onNodeDrag}
      onNodeDragStop={readOnly ? undefined : onNodeDragStop}
      onNodesChange={readOnly ? undefined : onNodesChange}
    >
      <Controls className="!border-border !bg-background [&>button]:!border-border [&>button]:!bg-background [&>button]:!fill-foreground" />
      <MiniMap className="!border-border !bg-background" nodeColor="hsl(var(--muted))" />
      <Background gap={12} size={1} />
      <Panel position="top-right">
        <div className="flex items-center gap-2">
          {!readOnly && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  className="bg-background/80 h-7 gap-1.5 backdrop-blur"
                  disabled={nodes.length === 0}
                  size="sm"
                  variant="outline"
                  onClick={handleAutoFormat}
                >
                  <AlignHorizontalDistributeCenter className="size-3.5" />
                  <span className="text-xs">Auto-format</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent>Arrange nodes in a clean left-to-right layout</TooltipContent>
            </Tooltip>
          )}
          <div className="bg-background/80 rounded border p-2 text-xs backdrop-blur">
            {nodes.length} Nodes | {edges.length} Edges
          </div>
        </div>
      </Panel>
      <Panel className="text-muted-foreground hidden text-xs sm:block" position="bottom-center">
        Click to select • Del to remove • Ctrl+Z undo • Ctrl+Y redo
      </Panel>
    </ReactFlow>
  )
}

interface WorkflowEditorProps {
  activeNodeId?: string | null
  readOnly?: boolean
}

export function WorkflowEditor({ activeNodeId, readOnly = false }: WorkflowEditorProps) {
  const [searchTerm, setSearchTerm] = useState("")
  const [pendingComponentAdd, setPendingComponentAdd] = useState<{
    requestId: number
    component: ComponentItem
  } | null>(null)
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const stored = localStorage.getItem(SIDEBAR_COLLAPSED_KEY)
    if (stored !== null) return stored === "true"
    // No saved preference yet: collapse to the icon rail on small screens so
    // the palette doesn't clip its labels or crowd out the canvas (<1024px).
    return typeof window !== "undefined" && window.innerWidth < 1024
  })

  const toggleCollapsed = () => {
    setIsCollapsed((prev) => {
      const newValue = !prev
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(newValue))
      return newValue
    })
  }

  // Collapse the palette to its icon rail when the viewport crosses into narrow
  // (<1024px) so it can't clip labels or crowd out the canvas. Doesn't persist,
  // so the desktop preference is preserved. Runs only on the narrow transition.
  const isNarrowViewport = useViewportBelow({ breakpoint: 1024 })
  const isPaletteCollapsed = isNarrowViewport || isCollapsed
  let paletteToggleTooltip = "Collapse sidebar"
  if (isNarrowViewport) {
    paletteToggleTooltip = "Compact palette on small screens"
  } else if (isPaletteCollapsed) {
    paletteToggleTooltip = "Expand sidebar"
  }
  const flowEmailDisabledReason = useMemo(() => {
    return getFlowEmailDisabledReason(SettingsService.getInstance().settings)
  }, [])

  useEffect(() => {
    if (isNarrowViewport) {
      setIsCollapsed(true)
    }
  }, [isNarrowViewport])

  // #11: collapsible palette categories. Default-collapse the secondary sections
  // (Tools, Data & Utilities) so the sidebar opens focused on Flow + AI; the user
  // can expand them and the choice persists.
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(() => {
    const stored = localStorage.getItem(CATEGORIES_COLLAPSED_KEY)
    if (stored) {
      try {
        return new Set(JSON.parse(stored) as string[])
      } catch {
        // fall through to default
      }
    }
    return new Set(["data", "tools"])
  })

  const toggleCategory = (category: string) => {
    setCollapsedCategories((prev) => {
      const next = new Set(prev)
      if (next.has(category)) {
        next.delete(category)
      } else {
        next.add(category)
      }
      localStorage.setItem(CATEGORIES_COLLAPSED_KEY, JSON.stringify([...next]))
      return next
    })
  }

  const onDragStart = (event: DragEvent, nodeType: string, data?: unknown) => {
    event.dataTransfer.setData("application/reactflow/type", nodeType)
    if (data) {
      event.dataTransfer.setData("application/reactflow/data", JSON.stringify(data))
    }
    event.dataTransfer.effectAllowed = "move"
  }

  const handlePaletteClick = useCallback(
    (component: ComponentItem) => {
      if (readOnly || component.disabled) return
      setPendingComponentAdd({ requestId: Date.now(), component })
    },
    [readOnly]
  )

  const handlePendingComponentAdded = useCallback((requestId: number) => {
    setPendingComponentAdd((pending) => (pending?.requestId === requestId ? null : pending))
  }, [])

  const filteredComponents = useMemo(() => {
    const components = WORKFLOW_COMPONENTS.map((component) => {
      if (component.type !== "email" || !flowEmailDisabledReason) return component
      return { ...component, disabled: true, disabledReason: flowEmailDisabledReason }
    })
    if (!searchTerm.trim()) return components
    const lowerSearch = searchTerm.toLowerCase()
    return components.filter(
      (component) =>
        component.label.toLowerCase().includes(lowerSearch) ||
        component.category.toLowerCase().includes(lowerSearch) ||
        CATEGORY_LABELS[component.category].toLowerCase().includes(lowerSearch)
    )
  }, [flowEmailDisabledReason, searchTerm])

  const groupedComponents = useMemo(() => {
    const groups: Record<ComponentItem["category"], ComponentItem[]> = {
      flow: [],
      ai: [],
      data: [],
      tools: [],
    }
    for (const component of filteredComponents) {
      groups[component.category].push(component)
    }
    return groups
  }, [filteredComponents])

  const nonEmptyCategories = CATEGORY_ORDER.filter((cat) => groupedComponents[cat].length > 0)

  const renderComponentItem = (component: ComponentItem) => {
    const isDisabled = readOnly || component.disabled
    const componentKey = `${component.type}-${component.label}`

    if (isPaletteCollapsed) {
      const collapsedClasses = cn(
        "flex size-10 items-center justify-center rounded-[8px] border bg-card shadow-sm",
        isDisabled
          ? "cursor-not-allowed opacity-50"
          : "cursor-grab transition-colors hover:bg-accent hover:text-accent-foreground",
        component.borderColor
      )

      const iconElement = (
        <div
          aria-disabled={isDisabled}
          aria-label={`Add ${component.label}`}
          className={collapsedClasses}
          draggable={!isDisabled}
          role="button"
          tabIndex={isDisabled ? -1 : 0}
          onClick={() => handlePaletteClick(component)}
          onDragStart={
            isDisabled ? undefined : (event) => onDragStart(event, component.type, component.data)
          }
          onKeyDown={(event) => {
            if (isDisabled) return
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault()
              handlePaletteClick(component)
            }
          }}
        >
          <component.icon className="size-4 shrink-0" />
        </div>
      )

      return (
        <Tooltip key={componentKey}>
          <TooltipTrigger asChild>{iconElement}</TooltipTrigger>
          <TooltipContent side="right">
            {component.label}
            {component.comingSoon && " (Coming soon)"}
            {component.disabledReason && (
              <span className="block max-w-48 text-xs opacity-80">{component.disabledReason}</span>
            )}
          </TooltipContent>
        </Tooltip>
      )
    }

    const baseClasses = "flex items-center gap-2 rounded-[8px] border bg-card p-3 text-sm shadow-sm"
    const interactiveClasses = isDisabled
      ? "cursor-not-allowed opacity-50"
      : "cursor-grab transition-colors hover:bg-accent hover:text-accent-foreground"

    const item = (
      <div
        key={componentKey}
        aria-disabled={isDisabled}
        aria-label={`Add ${component.label}`}
        className={cn(baseClasses, interactiveClasses, component.borderColor)}
        draggable={!isDisabled}
        role="button"
        tabIndex={isDisabled ? -1 : 0}
        onClick={() => handlePaletteClick(component)}
        onDragStart={
          isDisabled ? undefined : (event) => onDragStart(event, component.type, component.data)
        }
        onKeyDown={(event) => {
          if (isDisabled) return
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault()
            handlePaletteClick(component)
          }
        }}
      >
        <component.icon className="size-4 shrink-0" />
        {component.label}
      </div>
    )

    // #11: every expanded item gets a hover description.
    const description =
      component.type === "tool"
        ? ((component.data?.description as string | undefined) ?? component.label)
        : (NODE_DESCRIPTIONS[component.type] ?? component.label)
    const tooltipDescription = component.disabledReason ?? description

    return (
      <Tooltip key={componentKey}>
        <TooltipTrigger asChild>{item}</TooltipTrigger>
        <TooltipContent className="max-w-56" side="right">
          {tooltipDescription}
          {component.comingSoon && " (Coming soon)"}
        </TooltipContent>
      </Tooltip>
    )
  }

  return (
    <div className="flex h-full w-full overflow-hidden">
      {/* Sidebar for nodes */}
      <div
        className={cn(
          "bg-background flex h-full flex-col overflow-hidden border-r transition-[width] duration-200 ease-in-out",
          isPaletteCollapsed ? "w-16" : "w-64"
        )}
      >
        <div
          className={cn(
            "shrink-0 border-b transition-[padding] duration-200",
            isPaletteCollapsed ? "flex items-center justify-center p-2" : "space-y-3 p-4"
          )}
        >
          <div
            className={cn(
              "flex items-center",
              isPaletteCollapsed ? "justify-center" : "justify-between"
            )}
          >
            {!isPaletteCollapsed && <h3 className="font-semibold">Components</h3>}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  className="size-8 shrink-0"
                  disabled={isNarrowViewport}
                  size="icon"
                  variant="ghost"
                  aria-label={
                    isPaletteCollapsed ? "Expand components palette" : "Collapse components palette"
                  }
                  onClick={toggleCollapsed}
                >
                  {isPaletteCollapsed ? (
                    <ChevronRight className="size-4" />
                  ) : (
                    <ChevronLeft className="size-4" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">{paletteToggleTooltip}</TooltipContent>
            </Tooltip>
          </div>
          {!isPaletteCollapsed && (
            <div className="relative">
              <Search className="text-muted-foreground absolute top-1/2 left-2.5 size-4 -translate-y-1/2" />
              <Input
                className="h-8 pr-8 pl-9 text-sm"
                placeholder="Search components..."
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
              {searchTerm && (
                <Button
                  aria-label="Clear search"
                  className="text-muted-foreground hover:text-foreground absolute top-1/2 right-1 size-6 -translate-y-1/2"
                  size="icon"
                  type="button"
                  variant="ghost"
                  onClick={() => setSearchTerm("")}
                >
                  <X className="size-4" />
                </Button>
              )}
            </div>
          )}
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto">
          <div
            className={cn(
              "pb-16 transition-[padding] duration-200",
              isPaletteCollapsed ? "flex flex-col items-center gap-4 p-2" : "space-y-6 p-4"
            )}
          >
            {!isPaletteCollapsed && filteredComponents.length === 0 && (
              <p className="text-muted-foreground py-4 text-center text-sm">No components found</p>
            )}

            {nonEmptyCategories.map((category, categoryIndex) => {
              // While searching, force every section open so results always show.
              const categoryExpanded =
                Boolean(searchTerm.trim()) || !collapsedCategories.has(category)
              return (
                <div
                  key={category}
                  className={isPaletteCollapsed ? "flex flex-col items-center gap-2" : ""}
                >
                  {isPaletteCollapsed ? (
                    <>
                      {groupedComponents[category].map(renderComponentItem)}
                      {categoryIndex < nonEmptyCategories.length - 1 && (
                        <Separator className="my-1 w-10" />
                      )}
                    </>
                  ) : (
                    <>
                      <div className="space-y-2">
                        <button
                          aria-expanded={categoryExpanded}
                          className="text-muted-foreground hover:text-foreground focus-visible:ring-ring flex w-full items-center justify-between rounded text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
                          type="button"
                          onClick={() => toggleCategory(category)}
                        >
                          <span>{CATEGORY_LABELS[category]}</span>
                          <ChevronDown
                            className={cn(
                              "size-4 transition-transform",
                              categoryExpanded ? "" : "-rotate-90"
                            )}
                          />
                        </button>
                        {categoryExpanded && groupedComponents[category].map(renderComponentItem)}
                      </div>
                      {categoryIndex < nonEmptyCategories.length - 1 && (
                        <Separator className="mt-6" />
                      )}
                    </>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      </div>
      <div className="h-full min-h-0 flex-1">
        <ReactFlowProvider>
          <div className="h-full w-full">
            <WorkflowCanvas
              activeNodeId={activeNodeId}
              pendingComponentAdd={pendingComponentAdd}
              readOnly={readOnly}
              onPendingComponentAdded={handlePendingComponentAdded}
            />
          </div>
        </ReactFlowProvider>
      </div>
    </div>
  )
}
